import { Component, DoCheck, Input, OnDestroy, ViewChild } from '@angular/core';
import { DxContextMenuComponent, DxTreeListComponent } from "devextreme-angular";
import { LoginService } from "../../../../../Services/login.service";
import { CatalogService } from "../../../../../Services/CatalogManagement/catalog.service";
import { TreeItem as TreeElement } from "../../../../../Model/Dto/TreeItem";
import { ImportContainer } from "../../../../../Model/dataProcess/importContainer";
import { Category } from "../../../../../Model/Catalog/Category";
import { ImportContainerService } from "../../../../../Services/DataProcess/import-container.service";
import { GoldenRecordService } from "../../../../../Services/goldenRecord.service";
import { CategoryService } from "../../../../../Services/CatalogManagement/category.service";
import { AddItem } from "../../../../../Model/Dto/AddItem";
import ArrayStore from "devextreme/data/array_store";
import { SystemService } from "../../../../../Services/system.service";
import { TranslateService } from "@ngx-translate/core";
import { manualMappingOrigin } from "../../../../../Model/dataProcess/mappingOrigin";
import { SignalrService } from "../../../../../Services/signalr.service";
import { Subscription } from "rxjs";

export type CategorySelectItem = Category & { groupString: string };

@Component({
    selector: 'np-category-mapping-tab',
    templateUrl: './np-category-mapping-tab.component.html',
    styleUrl: './np-category-mapping-tab.component.css',
    standalone: false
})
export class NpCategoryMappingTabComponent implements DoCheck, OnDestroy {
    @ViewChild('treeList', { static: false }) treeList?: DxTreeListComponent;
    @ViewChild('rowMenu', { static: false }) rowMenu?: DxContextMenuComponent;

    @Input() container: ImportContainer;

    private previousActiveCatalogId: string;
    private isUpdatingActiveCatalog = false;

    rows: any;

    goldenRecordCatalogCategories: Record<string, Category>;
    goldenRecordCatalogCategoryItems: CategorySelectItem[];
    goldenRecordCategoryDataSource: any;
    goldenRecordCatalogId: string = "";

    menuVisible = false;

    menuRow?: any;
    childs: TreeElement[];
    importCategoryIdToGroupString: Record<string, string>;
    aiMappingRunning: boolean = false;
    protected mappingJobId?: string;
    private jobFinishedSubscription?: Subscription;
    addCategoryVisible: boolean = false;
    newCategoryName: string = "";

    constructor(
        public loginService: LoginService,
        public catalogService: CatalogService,
        public importContainerService: ImportContainerService,
        public goldenRecordService: GoldenRecordService,
        public categoryService: CategoryService,
        private systemService: SystemService,
        private translate: TranslateService,
        private signalrService: SignalrService,
    ) {
        this.addCategory = this.addCategory.bind(this);
        this.hideAddCategory = this.hideAddCategory.bind(this);
    }

    async ngOnInit(): Promise<void> {
        this.previousActiveCatalogId = this.container?.activeCatalogId;
        const customerId = this.loginService.currentCustomer.id;

        const goldenRecord = await this.goldenRecordService.getGoldenRecord(customerId);
        this.goldenRecordCatalogId = goldenRecord.catalogId;
        this.goldenRecordCatalogCategories = await this.catalogService.getAllCategoriesLeafs(this.goldenRecordCatalogId, customerId);
        this.goldenRecordCatalogCategoryItems = Object.entries(this.goldenRecordCatalogCategories)
            .map(([groupString, category]) => ({ ...category, groupString: groupString }));
        this.buildGoldenRecordCategoryDataSource();

        await this.loadImportCatalogCategories();

        this.rows = {
            load: async (loadOptions) => {
                let parentId = '0';
                if (loadOptions.parentIds && loadOptions.parentIds.length) {
                    parentId = loadOptions.parentIds[0];
                }

                const childs = await this.catalogService.getChilds(
                    this.container.activeCatalogId,
                    parentId,
                    this.loginService.currentUser.customerId,
                    loadOptions.filter,
                    this.catalogService.selectedStates,
                    this.loginService.productSearchOptions,
                    this.catalogService.selectedStatesMode
                );

                const filtered = childs.filter(x => x.type === 'cat' || x.type === 'grp');

                return filtered.map(item => {
                    const hasChilds =
                        (item.categoryCount === 0 && (item.productCount ?? 0) > 0)
                            ? false
                            : (item.hasChilds ?? true);

                    const groupPath = this.importCategoryIdToGroupString[item.id];
                    const goldenRecordCategoryStableId = groupPath
                        ? (this.container.categoryMappings?.[groupPath] ?? null)
                        : null;

                    return {
                        ...item,
                        hasChilds,
                        goldenRecordCategoryStableId,
                        state: goldenRecordCategoryStableId ? 'mapped' : 'unmapped',
                    };
                });
            }
        };
    }

    ngDoCheck(): void {
        const currentId = this.container?.activeCatalogId;
        if (currentId !== this.previousActiveCatalogId && !this.isUpdatingActiveCatalog) {
            this.previousActiveCatalogId = currentId;
            if (currentId) {
                this.isUpdatingActiveCatalog = true;
                this.loadImportCatalogCategories()
                    .then(() => this.treeList?.instance?.refresh())
                    .finally(() => this.isUpdatingActiveCatalog = false);
            }
        }
    }

    private buildGoldenRecordCategoryDataSource(): void {
        this.goldenRecordCategoryDataSource = {
            store: new ArrayStore({
                key: 'stableId',
                data: this.goldenRecordCatalogCategoryItems ?? []
            }),
            paginate: true,
            pageSize: 100
        };
    }

    private async loadImportCatalogCategories(): Promise<void> {
        const customerId = this.loginService.currentCustomer.id;
        const importCatalogCategories = await this.catalogService.getAllCategories(this.container.activeCatalogId, customerId);
        this.importCategoryIdToGroupString = Object.fromEntries(
            Object.entries(importCatalogCategories).map(([groupString, category]) => [category.id, groupString])
        );
    }

    async onMappingChanged(cell: any, e: any): Promise<void> {
        const row = cell.data;
        const newValue = e.value;

        row.goldenRecordCategoryStableId = newValue;
        await this.upsertCategoryMappingForRow(row, newValue);
        row.state = newValue ? 'mapped' : 'unmapped';
    }

    openRowMenu(e: any, cell: any): void {
        this.menuRow = cell.data;
        const targetElement = e.element as HTMLElement;
        this.rowMenu?.instance.option('target', targetElement);
        this.rowMenu?.instance.show();
    }

    onMenuItemClick(e: any): void {
        this.addCategoryVisible = true;
        this.menuVisible = false;
    }

    hideAddCategory(): void {
        this.addCategoryVisible = false;
    }

    async addCategory(): Promise<void> {
        const parentCategory = this.goldenRecordCatalogCategoryItems.find(
            x => x.stableId === this.menuRow.goldenRecordCategoryStableId
        );
        const addItem = new AddItem(
            parentCategory?.id,
            this.loginService.currentCustomer.id,
            "",
            this.newCategoryName
        );

        await this.categoryService.addCategory(addItem);
        this.goldenRecordCatalogCategories = await this.catalogService.getAllCategoriesLeafs(this.goldenRecordCatalogId, this.loginService.currentCustomer.id);
        this.goldenRecordCatalogCategoryItems = Object.entries(this.goldenRecordCatalogCategories)
            .map(([groupString, cat]) => ({ ...cat, groupString: groupString }));
        this.buildGoldenRecordCategoryDataSource();
        this.addCategoryVisible = false;
    }

    async runAutoMapping(): Promise<void> {
        const openGroupStrings = Object.values(this.importCategoryIdToGroupString ?? {})
            .filter(groupString => !this.container.categoryMappings?.[groupString]);

        if (openGroupStrings.length === 0) {
            this.systemService.notifyInfo(this.translate.instant('Es konnte keine Zuordnung gefunden werden.'), 3000);
            return;
        }

        await this.suggestMappings(openGroupStrings);
    }

    async runAutoMappingFor(row: any): Promise<void> {
        const groupString = this.importCategoryIdToGroupString?.[row.id];
        if (!groupString) return;

        await this.suggestMappings([groupString]);
    }

    private async suggestMappings(groupStrings: string[]): Promise<void> {
        this.aiMappingRunning = true;
        try {
            const result = await this.categoryService.suggestCatalogMapping(groupStrings, this.container.id);
            await this.reloadContainerMappings();

            if (result.jobId) {
                await this.followMappingJob(result.jobId);
                return;
            }

            const mapped = result.suggestions.filter(suggestion => suggestion.isMapped);

            if (result.aiBudgetExceeded)
                this.systemService.notifyWarning(this.translate.instant('Das KI-Kostenlimit ist erreicht. Die restlichen Einträge wurden nicht automatisch zugeordnet.'), 6000);
            else if (mapped.length === 0)
                this.systemService.notifyInfo(this.translate.instant('Es konnte keine Zuordnung gefunden werden.'), 3000);
        } catch (e) {
            this.notifySuggestionFailure(e);
            await this.reloadContainerMappings().catch(() => undefined);
        } finally {
            if (!this.mappingJobId)
                this.aiMappingRunning = false;
        }
    }

    ngOnDestroy(): void {
        void this.stopFollowingMappingJob();
    }

    private async followMappingJob(jobId: string): Promise<void> {
        this.mappingJobId = jobId;
        try {
            await this.signalrService.startJobDetailUpdateFeed(jobId);
        } catch (e) {
            this.mappingJobId = undefined;
            throw e;
        }

        this.systemService.notifyInfo(
            this.translate.instant('Die automatische Zuordnung läuft im Hintergrund. Der Fortschritt ist in der Jobliste sichtbar.'), 5000);

        this.jobFinishedSubscription = this.signalrService.finishedJob$.subscribe(async finished => {
            if (finished.jobId !== this.mappingJobId) return;

            await this.stopFollowingMappingJob();
            await this.reloadContainerMappings();
        });
    }

    private async stopFollowingMappingJob(): Promise<void> {
        this.jobFinishedSubscription?.unsubscribe();
        this.jobFinishedSubscription = undefined;

        if (this.mappingJobId)
            await this.signalrService.removeJobDetailUpdate(this.mappingJobId);

        this.mappingJobId = undefined;
        this.aiMappingRunning = false;
    }

    private async reloadContainerMappings(): Promise<void> {
        const reloaded = await this.importContainerService.get(this.container.id, this.loginService.currentCustomer.id);
        this.container.categoryMappings = reloaded?.categoryMappings ?? {};
        this.container.categoryMappingOrigins = reloaded?.categoryMappingOrigins ?? {};
        this.treeList?.instance?.refresh();
    }

    private notifySuggestionFailure(e: any): void {
        if (e?.status === 400 && typeof e.error === 'string') {
            this.systemService.notifyWarning(e.error, 6000);
            return;
        }
        if (e?.status === 429) {
            this.systemService.notifyWarning(this.translate.instant('Die automatische Zuordnung läuft bereits. Bitte warten.'), 4000);
            return;
        }
        this.systemService.notifyWarning(this.translate.instant('Die automatische Zuordnung ist fehlgeschlagen.'), 4000);
    }

    private async upsertCategoryMappingForRow(row: any, goldenStableId: string): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;
        if (!customerId) return;

        const importGroupString = this.importCategoryIdToGroupString[row.id];
        if (!importGroupString) return;

        this.container.categoryMappings ??= {};
        this.container.categoryMappingOrigins ??= {};

        if (goldenStableId) {
            this.container.categoryMappings[importGroupString] = goldenStableId;
            this.container.categoryMappingOrigins[importGroupString] = manualMappingOrigin();
        } else {
            delete this.container.categoryMappings[importGroupString];
            delete this.container.categoryMappingOrigins[importGroupString];
        }

        await this.importContainerService.update(this.container, customerId);
    }
}
